import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Beaker,
  Check,
  ChevronDown,
  FlaskConical,
  Lock,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { useAuth } from '@/hooks/auth/useAuth';
import { api } from '@/lib/api';
import { orgPluginsQueryKey } from '@/hooks/plugins/useOrgPlugins';
import type { PluginReleaseChannel } from '@/lib/types';

/**
 * Tier values are cumulative: picking 'alpha' means the org wants
 * everything at or below alpha risk, so we save `['beta', 'alpha']`.
 * 'stable' is the implicit zero — no pre-release plugins.
 */
type Tier = 'stable' | 'beta' | 'alpha' | 'preview';

interface TierSpec {
  value: Tier;
  /** Channels written to the API when this tier is selected. */
  channels: readonly PluginReleaseChannel[];
  label: string;
  /** Body of the radio card. */
  description: string;
  /** Summary shown in the collapsed header when this tier is active. */
  summary: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Tailwind classes applied to the icon chip so the dot reads at a glance. */
  iconWrapper: string;
}

const TIERS: readonly TierSpec[] = [
  {
    value: 'stable',
    channels: [],
    label: 'Stable only',
    description: 'Released plugins only. No pre-release access.',
    summary: 'Stable plugins only',
    icon: ShieldCheck,
    iconWrapper: 'bg-muted/40 text-muted-foreground ring-1 ring-inset ring-border/50',
  },
  {
    value: 'beta',
    channels: ['beta'],
    label: 'Beta',
    description:
      'Feature-complete plugins that are broadly stable. Suitable for production use; expect occasional bug-fix updates.',
    summary: 'Beta enabled',
    icon: Beaker,
    iconWrapper: 'bg-status-info/10 text-status-info ring-1 ring-inset ring-status-info/30',
  },
  {
    value: 'alpha',
    channels: ['beta', 'alpha'],
    label: 'Alpha',
    description:
      'Functional but rough plugins still under active development. APIs and UI may change before release. Includes Beta plugins.',
    summary: 'Alpha and Beta enabled',
    icon: FlaskConical,
    iconWrapper:
      'bg-status-warning/10 text-status-warning ring-1 ring-inset ring-status-warning/30',
  },
  {
    value: 'preview',
    channels: ['beta', 'alpha', 'preview'],
    label: 'Preview',
    description:
      'Earliest look at brand-new plugins. May be incomplete, broken, or change shape without notice. Includes Alpha and Beta plugins.',
    summary: 'All pre-release channels enabled',
    icon: Sparkles,
    iconWrapper: 'bg-status-error/10 text-status-error ring-1 ring-inset ring-status-error/30',
  },
];

const TIER_BY_VALUE: Record<Tier, TierSpec> = Object.fromEntries(
  TIERS.map((spec) => [spec.value, spec]),
) as Record<Tier, TierSpec>;

const RISK_ORDER: Record<PluginReleaseChannel, number> = { beta: 1, alpha: 2, preview: 3 };

/**
 * Reduce the org's enabled channels to a single tier by picking the
 * riskiest channel currently turned on. Saving any tier writes the
 * cumulative array, so legacy non-contiguous sets (e.g. `['preview']`
 * with no beta) get normalized on the next change.
 */
function deriveTier(enabled: readonly PluginReleaseChannel[]): Tier {
  if (enabled.length === 0) return 'stable';
  let max: PluginReleaseChannel = enabled[0];
  for (const channel of enabled) {
    if (RISK_ORDER[channel] > RISK_ORDER[max]) max = channel;
  }
  return max;
}

export interface ReleaseChannelOptInProps {
  /** Active organization id used for the PATCH call. */
  orgId: string;
  /** Channels the org is currently opted into. */
  enabledChannels: readonly PluginReleaseChannel[];
  /**
   * When false, the section renders a read-only tier list and a lock
   * badge explaining that the active member can't change tiers. Drives
   * off the `org:manage` permission at the call site.
   */
  canManage: boolean;
  /**
   * Test-only override for the initial collapsed state. Production
   * callers should leave this unset so the section is closed on first
   * render and the user opts in to the picker.
   */
  defaultOpen?: boolean;
}

/**
 * Org-manager control for enrolling the active organization in
 * pre-release plugin channels. Released plugins are unconditional and
 * don't appear here.
 *
 * The control collapses by default with a one-line summary of the
 * active tier so the more common stable case stays out of the way on
 * the plugins page. Expanding it reveals a single-select tier picker
 * (stable / beta / alpha / preview); each tier is cumulative, so
 * picking alpha unlocks beta too. The picker writes the cumulative
 * channel array to `/organizations/:id` with optimistic auth refresh
 * and per-tier pending state. Members without `org:manage` see the
 * same layout in read-only mode.
 */
export function ReleaseChannelOptIn({
  orgId,
  enabledChannels,
  canManage,
  defaultOpen = false,
}: ReleaseChannelOptInProps) {
  const queryClient = useQueryClient();
  const { refreshSession } = useAuth();
  const currentTier = useMemo(() => deriveTier(enabledChannels), [enabledChannels]);
  const [pendingTier, setPendingTier] = useState<Tier | null>(null);
  const [open, setOpen] = useState(defaultOpen);

  const mutation = useMutation({
    mutationFn: (next: readonly PluginReleaseChannel[]) =>
      api.updateOrganization(orgId, { enabledReleaseChannels: [...next] }),
    onSuccess: async () => {
      // Refresh `/auth/me` so `activeOrganization.enabledReleaseChannels`
      // reflects the new tier everywhere; invalidate the per-org plugins
      // list so newly-available plugins show up in the picker below.
      await refreshSession();
      void queryClient.invalidateQueries({ queryKey: orgPluginsQueryKey(orgId) });
    },
    onError: (err) => {
      toast.error('Failed to update release channels', {
        description: err instanceof Error ? err.message : undefined,
      });
    },
  });

  function selectTier(tier: Tier) {
    if (!canManage || tier === currentTier) return;
    setPendingTier(tier);
    mutation.mutate(TIER_BY_VALUE[tier].channels, {
      onSettled: () => setPendingTier(null),
    });
  }

  const activeSpec = TIER_BY_VALUE[currentTier];
  const ActiveIcon = activeSpec.icon;

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section
        data-testid="release-channels-opt-in"
        data-tier={currentTier}
        aria-label="Pre-release plugin channels"
        className="rounded-xl border border-border/40 bg-card/40"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="release-channels-toggle"
            aria-expanded={open}
            aria-controls="release-channels-panel"
            className="flex w-full items-center gap-3 rounded-xl p-4 text-left transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5"
          >
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                activeSpec.iconWrapper,
              )}
              aria-hidden
            >
              <ActiveIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <SectionLabel as="span" tone="strong">
                  Pre-release channels
                </SectionLabel>
                {!canManage && (
                  <span
                    data-testid="release-channels-locked"
                    className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground"
                  >
                    <Lock className="h-3 w-3" />
                    Manager only
                  </span>
                )}
              </div>
              <p
                data-testid="release-channels-summary"
                className="mt-0.5 truncate text-xs text-muted-foreground/90"
              >
                {activeSpec.summary}
              </p>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                open && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent id="release-channels-panel">
          <div className="border-t border-border/40 p-4 sm:p-5">
            <p className="mb-3 text-xs text-muted-foreground/90">
              Released plugins are available to everyone. Pick the riskiest tier you want to unlock
              for this organization; each tier includes everything more stable.
            </p>
            {/* A div, not <ul>: the li children's implicit `listitem` role
                is invalid inside a radiogroup (axe `listitem`). */}
            <div
              role="radiogroup"
              aria-label="Pre-release channel tier"
              className="flex flex-col gap-2"
            >
              {TIERS.map((spec) => (
                <TierRow
                  key={spec.value}
                  spec={spec}
                  selected={spec.value === currentTier}
                  pending={pendingTier === spec.value}
                  disabled={!canManage}
                  onSelect={() => selectTier(spec.value)}
                />
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

interface TierRowProps {
  spec: TierSpec;
  selected: boolean;
  pending: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function TierRow({ spec, selected, pending, disabled, onSelect }: TierRowProps) {
  const Icon = spec.icon;
  return (
    <div>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={disabled || pending}
        onClick={onSelect}
        data-testid={`release-channels-tier-${spec.value}`}
        data-selected={selected || undefined}
        className={cn(
          'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selected
            ? 'border-foreground/40 bg-muted/30'
            : 'border-border/40 bg-transparent hover:border-border hover:bg-muted/20',
          disabled && 'cursor-not-allowed opacity-60 hover:border-border/40 hover:bg-transparent',
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            spec.iconWrapper,
          )}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground/90">{spec.label}</p>
          <p className="mt-0.5 text-xs text-muted-foreground/90">{spec.description}</p>
        </div>
        <span
          aria-hidden
          className={cn(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
            selected
              ? 'border-foreground bg-foreground text-background'
              : 'border-border/60 bg-transparent',
          )}
        >
          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
      </button>
    </div>
  );
}
